import {
  Box,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Popover,
} from '@mui/material';
import type { AlertColor } from '@mui/material/Alert';
import { Clock, Edit3, History, Sparkles, Upload } from 'lucide-react';
import React, { useEffect, useRef, useState, useCallback } from 'react';

import ReactMarkdown from 'react-markdown';
import { useSelector } from 'react-redux';
import remarkGfm from 'remark-gfm';
import apiReq from 'core/api';
import store from 'redux/store';
import { Button as ShellButton, EmptyState, SplitPane } from 'components/AppShell';
import BusinessInfoEditModal from './BusinessInfoEditModal';
import ContextDocuments from './ContextDocuments';
import CustomPrompt from './CustomPrompt';
import GeneratedListBase from './GeneratedList';
import SamplesTTList from './SamplesTTList';

const GeneratedList = GeneratedListBase as React.ComponentType<{
  selectedTechnicalTask?: TechnicalTaskItem | undefined;
  onSelectItem: (item: TechnicalTaskItem | undefined) => void;
  reloadKey?: number;
  compact?: boolean;
}>;

type RootState = ReturnType<typeof store.getState>;
interface ReducerProject {
  id?: string | number;
  [key: string]: unknown;
}

export interface TechnicalTaskItem {
  id: number;
  name?: string;
  doc_content?: string;
  [key: string]: unknown;
}

const GENERATION_TASK_STORAGE_KEY = 'tz_generation_task';

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      color: 'var(--shell-text-tertiary)',
      padding: '6px 12px 4px',
    }}
  >
    {children}
  </div>
);

function saveGenerationTask(taskId: string, projectId: string | number) {
  localStorage.setItem(GENERATION_TASK_STORAGE_KEY, JSON.stringify({ taskId, projectId }));
}

function clearGenerationTask() {
  localStorage.removeItem(GENERATION_TASK_STORAGE_KEY);
}

function getStoredGenerationTask(): { taskId: string; projectId: string } | null {
  try {
    const raw = localStorage.getItem(GENERATION_TASK_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as { taskId: string; projectId: string };
  } catch {
    clearGenerationTask();
    return null;
  }
}

export default function Generator(): React.ReactElement {
  const currProject = useSelector(
    (state: RootState) => (state.reducer.project as ReducerProject | undefined)?.id
  );

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>('success');
  const [selectedTechnicalTask, setSelectedTechnicalTask] = useState<
    TechnicalTaskItem | undefined
  >();
  const [useBusinessRequirements, setUseBusinessRequirements] = useState(false);
  const [userCustomPrompt, setUserCustomPrompt] = useState(true);
  const [editBusinessOpen, setEditBusinessOpen] = useState(false);
  const [isSampleTTSelected, setIsSampleTTSelected] = useState(false);
  const [isContextDocumentsSelected, setIsContextDocumentsSelected] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSampleUploadDragging, setIsSampleUploadDragging] = useState(false);
  const sampleFileInputRef = useRef<HTMLInputElement>(null);
  const [samplesReloadKey, setSamplesReloadKey] = useState(0);
  const [generatedListReloadKey, setGeneratedListReloadKey] = useState(0);

  const onSelectItem = useCallback((item: TechnicalTaskItem | undefined) => {
    setSelectedTechnicalTask(item);
  }, []);

  const [loading, setLoading] = useState(false);
  const [isDisabledGenerate, setIsDisabledGenerate] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasRestoredPolling = useRef(false);
  const [activeView, setActiveView] = useState<'prompt' | 'result'>('prompt');
  const [historyAnchor, setHistoryAnchor] = useState<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (
      userCustomPrompt ||
      useBusinessRequirements ||
      isSampleTTSelected ||
      isContextDocumentsSelected
    ) {
      setIsDisabledGenerate(false);
    } else {
      setIsDisabledGenerate(true);
    }
  }, [userCustomPrompt, useBusinessRequirements, isSampleTTSelected, isContextDocumentsSelected]);

  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  const startStatusPolling = useCallback(
    (taskId: string, immediate = false) => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }

      const checkStatus = async () => {
        try {
          const status = await apiReq<{ status?: string }>(
            `projects/specification-generator/status?taskId=${taskId}`,
            'GET'
          );
          if (status?.status === 'done') {
            if (pollRef.current) {
              clearInterval(pollRef.current);
              pollRef.current = null;
            }
            clearGenerationTask();
            setLoading(false);
            try {
              const latest = await apiReq<{ ai_answer?: TechnicalTaskItem }>(
                `projects/latest-specification?project_id=${currProject}`,
                'GET'
              );
              const list = await apiReq<TechnicalTaskItem[]>(
                `projects/specifications-history?project_id=${currProject}`,
                'GET'
              );
              const items = Array.isArray(list) ? list : [];
              const latestId = latest?.ai_answer?.id;
              const fullItem = latestId != null ? items.find((i) => i.id === latestId) : items[0];
              if (fullItem) {
                setSelectedTechnicalTask(fullItem);
              } else if (latest?.ai_answer) {
                setSelectedTechnicalTask(latest.ai_answer);
              }
            } catch {
              // при ошибке получения latest всё равно уведомляем о завершении
            }
            setGeneratedListReloadKey((k) => k + 1);
            setSnackbarMessage('Генерация завершена');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
          }
        } catch (error) {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
          clearGenerationTask();
          setLoading(false);
          setSnackbarMessage(
            `Ошибка статуса генерации: ${error instanceof Error ? error.message : String(error)}`
          );
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      };

      if (immediate) {
        void checkStatus();
      }

      pollRef.current = setInterval(() => void checkStatus(), 5000);
    },
    [currProject]
  );

  useEffect(() => {
    if (!currProject || hasRestoredPolling.current) return;
    hasRestoredPolling.current = true;

    const stored = getStoredGenerationTask();
    if (!stored) return;

    if (String(stored.projectId) === String(currProject)) {
      setLoading(true);
      startStatusPolling(stored.taskId, true);
    } else {
      clearGenerationTask();
    }
  }, [currProject, startStatusPolling]);

  const pickSamplePdf = useCallback((file: File | undefined) => {
    if (!file) {
      setSelectedFile(null);
      setFileName('');
      return;
    }
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setUploadError('Нужен файл в формате PDF');
      setSelectedFile(null);
      setFileName('');
      return;
    }
    setSelectedFile(file);
    setFileName(file.name);
    setUploadError('');
    setUploadSuccess(false);
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      pickSamplePdf(event.target.files?.[0]);
    },
    [pickSamplePdf]
  );

  const handleSampleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsSampleUploadDragging(false);
      if (isUploading) return;
      pickSamplePdf(e.dataTransfer.files?.[0]);
    },
    [isUploading, pickSamplePdf]
  );

  const handleSampleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isUploading) setIsSampleUploadDragging(true);
    },
    [isUploading]
  );

  const handleSampleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSampleUploadDragging(false);
  }, []);

  const openSampleFilePicker = useCallback(() => {
    if (!isUploading) sampleFileInputRef.current?.click();
  }, [isUploading]);

  const handleUpload = useCallback(async () => {
    if (isUploading) return;
    if (!selectedFile) {
      setUploadError('Сначала выберите PDF-файл');
      return;
    }
    if (!currProject) {
      setUploadError('Выберите текущий проект проект');
      return;
    }
    setUploadError('');
    setIsUploading(true);

    const formData = new FormData();
    formData.append('document', selectedFile);
    formData.append('project_id', String(currProject));

    try {
      const uploadRes = await apiReq<{ task_id?: string; taskId?: string }>(
        'projects/documents/upload',
        'POST',
        formData,
        true
      );
      const taskId = uploadRes?.task_id ?? uploadRes?.taskId;
      if (!taskId) {
        throw new Error('task_id отсутствует в ответе сервера');
      }

      const pollUntilCompleted = async (): Promise<void> => {
        const maxAttempts = 60;
        let attempt = 0;
        let delayMs = 1500;
        while (attempt < maxAttempts) {
          const statusRes = await apiReq<{ status?: string; message?: string }>(
            `projects/documents/upload/status?taskId=${taskId}`,
            'GET'
          );
          const status = statusRes?.status;
          if (status === 'done') return;
          if (status === 'failed' || status === 'error') {
            const msg = statusRes?.message ?? 'Ошибка обработки файла';
            throw new Error(msg);
          }
          await new Promise((r) => setTimeout(r, delayMs));
          delayMs = Math.min(10000, Math.floor(delayMs * 1.5));
          attempt += 1;
        }
        throw new Error('Таймаут ожидания завершения загрузки');
      };

      await pollUntilCompleted();

      setSamplesReloadKey((k) => k + 1);

      setIsUploading(false);
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 3000);
      setUploadError('');
      setSelectedFile(null);
      setFileName('');
      if (sampleFileInputRef.current) sampleFileInputRef.current.value = '';
    } catch (err) {
      console.error('Upload error:', err);
      setUploadError(
        err instanceof Error ? err.message : 'Ошибка загрузки/обработки файла. Попробуйте еще раз.'
      );
      setIsUploading(false);
      setUploadSuccess(false);
    }
  }, [isUploading, selectedFile, currProject]);

  const handleGenerate = useCallback(() => {
    setLoading(true);
    apiReq<{ task_id?: string }>(
      `projects/specification-generator?project_id=${currProject}${useBusinessRequirements ? `&useBusinessRequirements=${useBusinessRequirements}` : ''}${userCustomPrompt ? `&usePrompt=${userCustomPrompt}` : ''}`,
      'POST'
    )
      .then((res) => {
        if (res?.task_id) {
          if (currProject != null) {
            saveGenerationTask(res.task_id, currProject);
          }
          setSnackbarMessage('Генерация запущена');
          setSnackbarSeverity('info');
          setSnackbarOpen(true);
          startStatusPolling(res.task_id);
        } else {
          setLoading(false);
          setSnackbarMessage('Не получен task_id при запуске генерации');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      })
      .catch((error: unknown) => {
        setLoading(false);
        setSnackbarMessage(
          `Ошибка запуска генерации: ${error instanceof Error ? error.message : String(error)}`
        );
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      });
  }, [currProject, useBusinessRequirements, userCustomPrompt, startStatusPolling]);

  const handleSnackbarClose = useCallback(
    (_event: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') {
        return;
      }
      setSnackbarOpen(false);
    },
    []
  );

  const sourcesCount =
    Number(userCustomPrompt) +
    Number(useBusinessRequirements) +
    Number(isSampleTTSelected) +
    Number(isContextDocumentsSelected);

  const configPanel = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'var(--shell-bg-canvas)',
      }}
    >
      <div
        style={{
          padding: '10px 12px',
          borderBottom: '1px solid var(--shell-border-subtle)',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--shell-bg-surface)',
        }}
      >
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--shell-text-primary)',
          }}
        >
          Конфигурация
        </span>
        <span
          style={{
            fontSize: 11,
            color: 'var(--shell-text-tertiary)',
          }}
        >
          источников: {sourcesCount}
        </span>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        {/* Sources */}
        <SectionHeading>Источники</SectionHeading>
        <div style={{ padding: '0 8px 12px' }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={userCustomPrompt}
                onChange={() => setUserCustomPrompt(!userCustomPrompt)}
                sx={{ p: 0.5 }}
              />
            }
            label={<span style={{ fontSize: 13 }}>Общий промпт</span>}
            sx={{ display: 'flex', m: 0, py: 0.25 }}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={useBusinessRequirements}
                  onChange={() => setUseBusinessRequirements(!useBusinessRequirements)}
                  sx={{ p: 0.5 }}
                />
              }
              label={<span style={{ fontSize: 13 }}>Инструкция компании</span>}
              sx={{ m: 0, py: 0.25, flex: 1 }}
            />
            <button
              type="button"
              onClick={() => setEditBusinessOpen(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '2px 6px',
                background: 'transparent',
                border: 'none',
                color: 'var(--shell-accent-pressed)',
                fontSize: 11,
                fontWeight: 500,
                cursor: 'pointer',
                borderRadius: 4,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--shell-accent-subtle)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <Edit3 size={10} strokeWidth={1.75} />
              Изменить
            </button>
          </div>
        </div>

        {/* Samples */}
        <SectionHeading>Образцы ТЗ</SectionHeading>
        <div style={{ padding: '0 8px' }}>
          <SamplesTTList onSelectSampleTT={setIsSampleTTSelected} reloadKey={samplesReloadKey} />
        </div>

        <div style={{ padding: '8px 12px 12px' }}>
          <input
            ref={sampleFileInputRef}
            type="file"
            id="file-upload-tt"
            accept="application/pdf,.pdf"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <div
            role="button"
            tabIndex={0}
            aria-busy={isUploading}
            aria-label="Зона загрузки PDF"
            onClick={openSampleFilePicker}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openSampleFilePicker();
              }
            }}
            onDrop={handleSampleDrop}
            onDragOver={handleSampleDragOver}
            onDragLeave={handleSampleDragLeave}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              padding: '14px 12px',
              borderRadius: 8,
              border: `1px dashed ${
                isSampleUploadDragging
                  ? 'var(--shell-accent-primary)'
                  : 'var(--shell-border-default)'
              }`,
              background: isSampleUploadDragging
                ? 'var(--shell-accent-subtle)'
                : 'var(--shell-bg-surface)',
              cursor: isUploading ? 'default' : 'pointer',
              transition: 'border-color 120ms ease-out, background 120ms ease-out',
              outline: 'none',
              textAlign: 'center',
            }}
          >
            {isUploading ? (
              <>
                <CircularProgress size={20} sx={{ color: 'var(--shell-accent-primary)' }} />
                <span style={{ fontSize: 11, color: 'var(--shell-text-tertiary)' }}>Загрузка…</span>
              </>
            ) : (
              <>
                <Upload
                  size={18}
                  strokeWidth={1.5}
                  color={
                    isSampleUploadDragging
                      ? 'var(--shell-accent-primary)'
                      : 'var(--shell-text-tertiary)'
                  }
                />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'var(--shell-text-primary)',
                  }}
                >
                  Перетащите PDF или нажмите
                </span>
                {fileName && (
                  <span
                    style={{
                      fontSize: 11,
                      color: 'var(--shell-accent-pressed)',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={fileName}
                  >
                    {fileName}
                  </span>
                )}
              </>
            )}
          </div>

          {uploadError && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--shell-semantic-error)',
                marginTop: 6,
              }}
            >
              {uploadError}
            </div>
          )}
          {uploadSuccess && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--shell-accent-pressed)',
                marginTop: 6,
              }}
            >
              Файл добавлен в образцы
            </div>
          )}
          {selectedFile && (
            <ShellButton
              variant="primary"
              size="sm"
              fullWidth
              onClick={(e) => {
                e.stopPropagation();
                void handleUpload();
              }}
              disabled={isUploading}
              style={{ marginTop: 6 }}
            >
              Загрузить
            </ShellButton>
          )}
        </div>

        {/* Context docs */}
        <SectionHeading>Контекст</SectionHeading>
        <div style={{ padding: '0 8px 12px' }}>
          <ContextDocuments isDocumentsSelected={setIsContextDocumentsSelected} />
        </div>
      </div>

      {editBusinessOpen && (
        <BusinessInfoEditModal
          open={editBusinessOpen}
          onClose={() => setEditBusinessOpen(false)}
          onSave={undefined}
          projectId={currProject}
        />
      )}
    </div>
  );

  const editorPanel = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* View switcher */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '8px 12px',
          borderBottom: '1px solid var(--shell-border-subtle)',
          background: 'var(--shell-bg-surface)',
          flexShrink: 0,
        }}
      >
        <ViewTab
          active={activeView === 'prompt'}
          onClick={() => setActiveView('prompt')}
          label="Промпт"
        />
        <ViewTab
          active={activeView === 'result'}
          onClick={() => setActiveView('result')}
          label="Результат"
          badge={selectedTechnicalTask ? '✓' : undefined}
        />
        <div style={{ flex: 1 }} />
        {selectedTechnicalTask?.name && (
          <span
            style={{
              fontSize: 11,
              color: 'var(--shell-text-tertiary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 280,
            }}
          >
            {selectedTechnicalTask.name}
          </span>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16 }}>
        {activeView === 'prompt' ? (
          <CustomPrompt isDisabled={!userCustomPrompt} />
        ) : selectedTechnicalTask ? (
          <Box
            sx={{
              fontSize: 13.5,
              lineHeight: 1.6,
              color: 'var(--shell-text-primary)',
              '& h1': { fontSize: 18, fontWeight: 600, mt: 2, mb: 1 },
              '& h2': { fontSize: 15, fontWeight: 600, mt: 1.5, mb: 0.75 },
              '& h3': { fontSize: 14, fontWeight: 600, mt: 1, mb: 0.5 },
              '& p': { my: 0.75 },
              '& ul, & ol': { pl: 3, my: 0.75 },
              '& code': {
                background: 'var(--shell-bg-subtle)',
                padding: '1px 4px',
                borderRadius: 3,
                fontSize: '0.9em',
              },
              '& pre': {
                background: 'var(--shell-bg-subtle)',
                p: 1.5,
                borderRadius: 1,
                overflow: 'auto',
              },
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {selectedTechnicalTask?.doc_content ?? ''}
            </ReactMarkdown>
          </Box>
        ) : (
          <EmptyState
            title="Результата пока нет"
            description="Настройте источники слева и нажмите «Сгенерировать», либо выберите готовое ТЗ из истории."
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid var(--shell-border-subtle)',
          borderRadius: 'var(--shell-radius-lg)',
          background: 'var(--shell-bg-surface)',
          overflow: 'hidden',
        }}
      >
        <SplitPane left={configPanel} right={editorPanel} leftWidth={300} />

        {/* Bottom action bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 14px',
            borderTop: '1px solid var(--shell-border-subtle)',
            background: 'var(--shell-bg-surface)',
            flexShrink: 0,
          }}
        >
          <ShellButton
            variant="primary"
            leftIcon={
              loading ? (
                <CircularProgress size={12} sx={{ color: 'currentColor' }} />
              ) : (
                <Sparkles size={14} strokeWidth={1.75} />
              )
            }
            onClick={() => {
              handleGenerate();
              setActiveView('result');
            }}
            disabled={isDisabledGenerate || loading}
          >
            {loading ? 'Генерация…' : 'Сгенерировать'}
          </ShellButton>
          {isDisabledGenerate && (
            <span style={{ fontSize: 11, color: 'var(--shell-semantic-warning)' }}>
              Выберите хотя бы один источник
            </span>
          )}
          <div style={{ flex: 1 }} />
          <ShellButton
            variant="secondary"
            leftIcon={<History size={14} strokeWidth={1.75} />}
            onClick={(e) => setHistoryAnchor(e.currentTarget as HTMLButtonElement)}
          >
            История
          </ShellButton>
        </div>
      </Box>

      <Popover
        open={Boolean(historyAnchor)}
        anchorEl={historyAnchor}
        onClose={() => setHistoryAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: -0.5,
              width: 360,
              maxHeight: 480,
              border: '1px solid var(--shell-border-subtle)',
              boxShadow: 'var(--shell-shadow-overlay)',
              borderRadius: 'var(--shell-radius-lg)',
              background: 'var(--shell-bg-surface)',
            },
          },
        }}
      >
        <div
          style={{
            padding: '10px 12px',
            borderBottom: '1px solid var(--shell-border-subtle)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--shell-text-primary)',
          }}
        >
          <Clock size={12} strokeWidth={1.75} color="var(--shell-text-tertiary)" />
          Сгенерированные ТЗ
        </div>
        <div style={{ maxHeight: 420, overflow: 'auto', padding: 6 }}>
          <GeneratedList
            onSelectItem={(item) => {
              onSelectItem(item);
              if (item) {
                setActiveView('result');
                setHistoryAnchor(null);
              }
            }}
            selectedTechnicalTask={selectedTechnicalTask}
            reloadKey={generatedListReloadKey}
            compact
          />
        </div>
      </Popover>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

function ViewTab({
  active,
  onClick,
  label,
  badge,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  badge?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 28,
        padding: '0 10px',
        border: 'none',
        background: active ? 'var(--shell-bg-subtle)' : 'transparent',
        color: active ? 'var(--shell-text-primary)' : 'var(--shell-text-secondary)',
        fontSize: 12,
        fontWeight: active ? 600 : 500,
        borderRadius: 6,
        cursor: 'pointer',
        transition: 'background var(--shell-transition-fast)',
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = 'var(--shell-bg-subtle)';
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = 'transparent';
      }}
    >
      {label}
      {badge && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 16,
            background: 'var(--shell-accent-primary)',
            color: 'var(--shell-accent-on-accent)',
            fontSize: 10,
            fontWeight: 700,
            borderRadius: 999,
          }}
        >
          {badge}
        </span>
      )}
    </button>
  );
}
